
class SynthKitEngine {
  constructor(context, analyser) {
    this.context = context;
    this.analyser = analyser;

    this.expressionPedalSetting = 1;
    this.expressionPedalMapping = [];
    this.kit = {};
    this.noiseBuffers = {};
    this.bufferSourcePool = this.initBufferSourcePool();
    this.oscillatorPool = this.initOscillatorPool();

    this.computeExpressionPedalMapping = this.computeExpressionPedalMapping.bind(this);
    this.connectToolChains = this.connectToolChains.bind(this);
    this.connectToolsToTargets = this.connectToolsToTargets.bind(this);
    this.connectToolToTargets = this.connectToolToTargets.bind(this);
    this.connectToolToTarget = this.connectToolToTarget.bind(this);
    this.createNoiseBuffer = this.createNoiseBuffer.bind(this);
    this.getBufferSource = this.getBufferSource.bind(this);
    this.getOscillator = this.getOscillator.bind(this);
    this.handleBufferSourceEnd = this.handleBufferSourceEnd.bind(this);
    this.handleOscillatorEnd = this.handleOscillatorEnd.bind(this);
    this.initBufferSourcePool = this.initBufferSourcePool.bind(this);
    this.initKit = this.initKit.bind(this);
    this.initOscillatorPool = this.initOscillatorPool.bind(this);
    this.loadADSREnvelope = this.loadADSREnvelope.bind(this);
    this.loadBufferSource = this.loadBufferSource.bind(this);
    this.loadGain = this.loadGain.bind(this);
    this.loadKitConfig = this.loadKitConfig.bind(this);
    this.loadOscillator = this.loadOscillator.bind(this);
    this.loadToolChains = this.loadToolChains.bind(this);
    this.makeDistortionCurve = this.makeDistortionCurve.bind(this);
    this.setZoneVolume = this.setZoneVolume.bind(this);
    this.setZonePan = this.setZonePan.bind(this);
    this.triggerZone = this.triggerZone.bind(this);
  }

  computeExpressionPedalMapping(zone, minDuration) {
    const maxDuration = zone.toolChain[0].duration;
    const duration = maxDuration - minDuration;
    let expressionPedalMapping = [];
    expressionPedalMapping[1] = maxDuration;
    for (let i = 2; i < 127; i++) {
      expressionPedalMapping[128 - i] = ((i / 127) * duration) + minDuration;
    }
    expressionPedalMapping[127] = minDuration;
    this.expressionPedalMapping = expressionPedalMapping;
  }

  connectToolChains(){
    const config = this.kitConfig.config;
    for(const component in config){
      for(const zone in config[component]){
        this.connectToolsToTargets(config[component][zone], this.kit[component][zone]);
      }
    }
  }

  connectToolsToTargets(zoneConfig, zone) {
    for(const tool of zoneConfig.toolChain){
      this.connectToolToTargets(tool, zone);
    }
  }

  connectToolToTargets(tool, zone) {
    for(const destinationId of tool.connectsTo){
      this.connectToolToTarget(zone[tool.id], zone[destinationId]);
    }
  }

  connectToolToTarget(source, target) {
    if(source){
      if(target) {
        source.connect(target);
      }
    }
  }

  createNoiseBuffer(bufferSizeFactor) {
    let bufferSize = this.context.sampleRate * bufferSizeFactor; // Use a larger buffer for more complex noise
    let buffer = this.context.createBuffer(1, bufferSize, this.context.sampleRate);
    let output = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }
    return buffer;
  }

  getBufferSource() {
    if (this.bufferSourcePool.length > 0) {
      return this.bufferSourcePool.pop();
    } else {
      console.warn("Buffer source pool empty, creating new buffer source");
      return this.context.createBufferSource();
    }
  }

  getOscillator() {
    if (this.oscillatorPool.length > 0) {
      return this.oscillatorPool.pop();
    } else {
      console.warn("Oscillator pool empty, creating new oscillator");
      return this.context.createOscillator();
    }
  }

  handleBufferSourceEnd(event) {
    setTimeout(() => {
      event.target.disconnect();
    }, 1000);
    this.bufferSourcePool.push(this.context.createBufferSource());
  }

  handleOscillatorEnd(event) {
    setTimeout(() => {
      event.target.disconnect();
    }, 1000);
    this.oscillatorPool.push(this.context.createOscillator());
  }

  initBufferSourcePool() {
    let bufferSourcePool = [];
    for (let i = 0; i < 100; i++) {
      bufferSourcePool.push(this.context.createBufferSource());
    }
    return bufferSourcePool;
  }

  initKit() {
    this.kit = {};
    for (let component in this.kitConfig.config) {
      this.kit[component] = {};
      for (let zone in this.kitConfig.config[component]) {
        if(component === "HiHat"){
          this.computeExpressionPedalMapping(this.kitConfig.config[component][zone]);
        }
        this.kit[component][zone] = {};
        this.kit[component][zone]["MainGainOut"] = this.context.createGain();
        this.kit[component][zone]["MainGainOut"].gain.value = 1;
        this.kit[component][zone]["MainGainOut"].connect(this.context.destination);
        this.kit[component][zone]["MainGainOut"].connect(this.analyser);

        this.kit[component][zone]["VelocityGainOut"] = this.context.createGain();
        this.kit[component][zone]["VelocityGainOut"].gain.value = 1;
        this.kit[component][zone]["VelocityGainOut"].connect(this.kit[component][zone]["MainGainOut"]);

        this.kit[component][zone]["MainPanOut"] = this.context.createStereoPanner();
        this.kit[component][zone]["MainPanOut"].pan.value = 0;
        this.kit[component][zone]["MainPanOut"].connect(this.kit[component][zone]["VelocityGainOut"]);
      }
    }
  }

  initOscillatorPool() {
    let oscillatorPool = [];
    for (let i = 0; i < 100; i++) {
      oscillatorPool.push(this.context.createOscillator());
    }
    return oscillatorPool;
  }

  loadADSREnvelope(component, zone, toolConfig, now) {
    const adsrEnvelope = this.context.createGain();
    // Attack
    adsrEnvelope.gain.linearRampToValueAtTime(1, now + toolConfig.attack);
    // Decay to sustain
    adsrEnvelope.gain.linearRampToValueAtTime(toolConfig.sustain, now + toolConfig.attack + toolConfig.decay);
    // Note held... then release
    adsrEnvelope.gain.setValueAtTime(toolConfig.sustain, now + toolConfig.attack + toolConfig.decay);
    adsrEnvelope.gain.linearRampToValueAtTime(0, now + toolConfig.attack + toolConfig.decay + toolConfig.release);
    this.kit[component][zone][toolConfig.id] = adsrEnvelope;

    return adsrEnvelope;
  }

  loadKitConfig(kitConfig) {
    this.kitConfig = kitConfig;
    this.initKit(kitConfig);
    this.loadToolChains();
    this.connectToolChains();
  }

  loadToolChains(){
    const config = this.kitConfig.config;
    for(let component in config){
      for(let zone in config[component]){
        for(let tool of config[component][zone].toolChain){
          switch(tool.type){
            case "adsrenvelope":
            case "oscillator":
            case "gain":
              // do nothing, this is handled on demand
              break;
            case "bufferSource":
              this.noiseBuffers[tool.id] = this.createNoiseBuffer(tool.bufferSizeFactor);
              break;
            case "biquadFilter":
              this.kit[component][zone][tool.id] = this.context.createBiquadFilter();
              this.kit[component][zone][tool.id].type = tool.filterType;
              this.kit[component][zone][tool.id].frequency.value = tool.frequency;
              this.kit[component][zone][tool.id].Q.value = tool.Q;
              this.kit[component][zone][tool.id].gain.value = tool.gain;
              break;
            case "convolver":
              this.kit[component][zone][tool.id] = this.context.createConvolver();
              this.kit[component][zone][tool.id].normalize = true;
              this.kit[component][zone][tool.id].buffer = this.noiseBuffers[tool.buffer];
              break;
            case "panner":
              this.kit[component][zone][tool.id] = this.context.createStereoPanner();
              this.kit[component][zone][tool.id].pan.value = tool.pan;
              break;
            case "waveShaper":
              this.kit[component][zone][tool.id] = this.context.createWaveShaper();
              this.kit[component][zone][tool.id].curve = this.makeDistortionCurve(tool.amount);
              this.kit[component][zone][tool.id].oversample = "4x";
              break;
            default:
              console.warn("Unknown tool type: " + tool.type);
          }
        }
      }
    }
  }

  loadBufferSource(component, zone, toolConfig) {
    let bufferSource = this.getBufferSource();
    bufferSource.buffer = this.noiseBuffers[toolConfig.id];
    bufferSource.loop = false;
    this.kit[component][zone][toolConfig.id] = bufferSource;
  }

  loadGain(component, zone, toolConfig, now) {
    let gain = this.context.createGain();
    gain.gain.value = toolConfig.gain;
    if(toolConfig.settings) {
      for (const setting of toolConfig.settings) {
        switch (setting.name) {
          case "setValueAtTime":
            gain.gain.setValueAtTime(setting.gain, now + setting.time);
            break;
          case "exponentialRampToValueAtTime":
            gain.gain.exponentialRampToValueAtTime(setting.gain, now + setting.time);
            break;
          case "linearRampToValueAtTime":
            gain.gain.linearRampToValueAtTime(setting.gain, now + setting.time);
            break;
          default:
            console.warn("Unknown setting name: " + setting.name, setting);
        }
      }
    }
    this.kit[component][zone][toolConfig.id] = gain;
  }

  loadOscillator(component, zone, toolConfig, now) {
    let osc = this.getOscillator();
    osc.frequency.value = toolConfig.frequency;
    osc.type = toolConfig.waveType;
    if(toolConfig.settings){
      for(const setting of toolConfig.settings){
        switch(setting.name){
          case "setValueAtTime":
            osc.frequency.setValueAtTime(setting.frequency, now + setting.time);
            break;
          case "exponentialRampToValueAtTime":
            osc.frequency.exponentialRampToValueAtTime(setting.frequency, now + setting.time);
            break;
          case "linearRampToValueAtTime":
            osc.frequency.linearRampToValueAtTime(setting.frequency, now + setting.time);
            break;
          default:
            console.warn("Unknown setting name: " + setting.name, setting);
        }
      }
    }
    this.kit[component][zone][toolConfig.id] = osc;
  }

  makeDistortionCurve(amount) {
    let k = typeof amount === 'number' ? amount : 50,
        n_samples = 44100,
        curve = new Float32Array(n_samples),
        deg = Math.PI / 180,
        i = 0,
        x;
    for ( ; i < n_samples; ++i ) {
      x = i * 2 / n_samples - 1;
      curve[i] = ( 3 + k ) * x * 20 * deg / ( Math.PI + k * Math.abs(x) );
    }
    return curve;
  }

  setZoneVolume(component, zone, volume) {
    this.kit[component][zone]["MainGainOut"].gain.value = volume;
  }

  setZonePan(component, zone, pan) {
    this.kit[component][zone]["MainPanOut"].pan.value = pan;
  }

  triggerZone(component, zone, volume) {
    this.kit[component][zone]["VelocityGainOut"].gain.value = volume;

    const kitZone = this.kit[component][zone];
    const kitConfigZone = this.kitConfig.config[component][zone];
    const now = this.context.currentTime;

    const toolChainConfig = kitConfigZone.toolChain;
    for(const toolConfig of toolChainConfig) {
      if(toolConfig.type === "gain"){
        this.loadGain(component, zone, toolConfig, now);
      }else if(toolConfig.type === "oscillator"){
        this.loadOscillator(component, zone, toolConfig, now);
      }else if(toolConfig.type === "bufferSource"){
        this.loadBufferSource(component, zone, toolConfig);
      }else if(toolConfig.type === "adsrenvelope"){
        this.loadADSREnvelope(component, zone, toolConfig, now);
      }
    }

    this.connectToolsToTargets(kitConfigZone, kitZone);

    for(const toolConfig of toolChainConfig) {
      if (toolConfig.type === "oscillator") {
        const tool = kitZone[toolConfig.id];
        tool.onended = this.handleOscillatorEnd;
        tool.start(now);
        tool.stop(now + toolConfig.duration);
      }else if(toolConfig.type === "bufferSource"){
        const tool = kitZone[toolConfig.id];
        tool.onended = this.handleBufferSourceEnd;
        tool.start(now);
        tool.stop(now + toolConfig.duration);
      }
    }
  }
}

export default SynthKitEngine;
